import type { ReactElement } from 'react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';

import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';

import { deleteCreditProfile, resendVerificationRetry } from 'app-components/flux/credit-profile/actions';
import { getCreditProfile } from 'app-components/flux/credit-profile/selectors';
import { rootUiActions } from 'app-components/flux/root-ui';

import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import VerificationStatusContent from './VerificationStatusContent';
import {
  CREDIT_SCORE_REGISTRATION_FORM_DIALOG_ID,
  CREDIT_SCORE_VERIFICATION_DIALOG_ID,
  CREDIT_SCORE_VERIFICATION_EVENTS_DIALOG_ID,
  CREDIT_SCORE_VERIFICATION_LOADER_DIALOG_ID,
  triggerCancelRegistrationDialog,
  triggerCreditReportUpdateDialog,
  triggerCreditScoreVerificationSentDialog,
} from './actions';
import { CREDIT_SCORE_DIALOG_EVENTS } from './constants';
import styles from './styles';
import { getLast4Digits } from './utils';

interface IVerificationEventsDialogProps {
  event: string;
  isSuccess?: boolean;
  flowId?: string;
}

type actionType = {
  key: string;
  label: string;
  btnVariant?: string;
  id?: string;
};

const useStyles = makeStyles()(styles);

const CreditScoreVerificationEventsDialog: React.FC<IVerificationEventsDialogProps> = (props) => {
  const { event, isSuccess = false, flowId } = props;

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const creditProfileData = useSelector(getCreditProfile);
  const phoneLastDigits = creditProfileData && getLast4Digits(creditProfileData?.creditProfile?.phoneNumber);

  const closeAllDialogsAndDeleteProfile = () => {
    dispatch(rootUiActions.removeDialog(CREDIT_SCORE_VERIFICATION_DIALOG_ID));
    dispatch(rootUiActions.removeDialog(CREDIT_SCORE_VERIFICATION_EVENTS_DIALOG_ID));
    dispatch(deleteCreditProfile({ creditProfileRegistrationId: creditProfileData?.id }, { props: { flowId } }));
  };

  const handleDialogAction = (actionKey: string) => {
    if (!creditProfileData?.creditProfile) {
      dispatch(rootUiActions.removeDialog(CREDIT_SCORE_VERIFICATION_DIALOG_ID));
      dispatch(rootUiActions.removeDialog(CREDIT_SCORE_VERIFICATION_LOADER_DIALOG_ID));
      dispatch(rootUiActions.removeDialog(CREDIT_SCORE_VERIFICATION_EVENTS_DIALOG_ID));
    } else if (actionKey === 'ok') {
      dispatch(rootUiActions.removeDialog(CREDIT_SCORE_VERIFICATION_EVENTS_DIALOG_ID));
      dispatch(triggerCreditScoreVerificationSentDialog(true, creditProfileData, flowId));
    } else if (actionKey === 'resend') {
      dispatch(rootUiActions.removeDialog(CREDIT_SCORE_VERIFICATION_EVENTS_DIALOG_ID));
      dispatch(
        resendVerificationRetry(
          {
            creditProfileRegistrationId: creditProfileData?.id,
          },
          { props: { flowId } },
        ),
      );
    } else if (actionKey === 'retry') {
      closeAllDialogsAndDeleteProfile();
    } else if (actionKey === 'home') {
      closeAllDialogsAndDeleteProfile();
      dispatch(rootUiActions.removeDialog(CREDIT_SCORE_REGISTRATION_FORM_DIALOG_ID));
      navigate('/');
    } else if (actionKey === 'back') {
      dispatch(rootUiActions.removeDialog(CREDIT_SCORE_VERIFICATION_EVENTS_DIALOG_ID));
      dispatch(triggerCreditReportUpdateDialog());
    } else if (actionKey === CREDIT_SCORE_DIALOG_EVENTS.refreshFailed) {
      dispatch(rootUiActions.removeDialog(CREDIT_SCORE_VERIFICATION_EVENTS_DIALOG_ID));
    } else {
      // all close button except CREDIT_SCORE_DIALOG_EVENTS.refreshFailed action
      dispatch(triggerCancelRegistrationDialog(flowId));
    }
  };

  const renderPhoneDigits = () => (
    <Box component='span' fontWeight={500}>
      {phoneLastDigits}
    </Box>
  );

  let title: string = '';
  let content: string | ReactElement<any> = '';
  let showCloseButton: boolean = true;
  const arrActions: actionType[] = [];
  switch (event) {
    case CREDIT_SCORE_DIALOG_EVENTS.resendLink:
      if (isSuccess) {
        title = 'Verification Link Sent';
        content = <>A new verification link was sent to your mobile number ending {renderPhoneDigits()}.</>;
        arrActions.push({ key: 'ok', label: 'OK', id: 'link-send-ok' });
      } else {
        title = 'Verification Link Not Sent';
        content = (
          <>
            There was an issue sending the verification link to your mobile number ending {renderPhoneDigits()}. Please
            try again.
          </>
        );
        arrActions.push({ key: 'resend', label: 'Resend', id: 'link-not-send-resend' });
      }
      break;
    case CREDIT_SCORE_DIALOG_EVENTS.verifying:
      content = <VerificationStatusContent isSuccess={isSuccess} />;
      showCloseButton = false;
      break;
    case CREDIT_SCORE_DIALOG_EVENTS.verificationFailed:
      title = 'Verification Link Failed';
      content = <>The verification link sent to your mobile number ending with {renderPhoneDigits()} failed.</>;
      arrActions.push({ key: 'resend', label: 'Resend', id: 'link-failed-resend' });
      break;
    case CREDIT_SCORE_DIALOG_EVENTS.registrationFailed:
      title = 'Registration Failed';
      content = (
        <>
          The verification link sent to your mobile number ending with {renderPhoneDigits()} failed to register. Please
          try again.
        </>
      );
      arrActions.push(
        { key: 'home', label: 'Home', btnVariant: 'outlined', id: 'registration-failed-home' },
        { key: 'retry', label: 'Retry', id: 'registration-failed-retry' },
      );
      break;
    case CREDIT_SCORE_DIALOG_EVENTS.refreshFailed:
      title = 'SSN Verification Failed';
      content = 'We could not verify the SSN number entered by you. Please check, then try again.';
      arrActions.push({ key: 'back', label: 'Back', id: 'ssn-failed-back' });
      break;
    default:
      break;
  }

  return (
    <StdDialog
      onClose={() => handleDialogAction(event)}
      open
      size='xs'
      disableBackdropClick
      showCloseButton={showCloseButton}
      title={title}
    >
      <DialogContent>
        {event === CREDIT_SCORE_DIALOG_EVENTS.verifying ? (
          content
        ) : (
          <Typography variant='paragraphRegular'>{content}</Typography>
        )}
      </DialogContent>
      {arrActions.length > 0 && (
        <DialogActions>
          {arrActions.map(({ key, label, btnVariant, id }) => (
            <QButton
              key={key}
              className={classes.btnDialogAction}
              variant={btnVariant || 'contained'}
              onClick={() => handleDialogAction(key)}
              id={`credit-score-${id}-btn`}
            >
              {label}
            </QButton>
          ))}
        </DialogActions>
      )}
    </StdDialog>
  );
};

export default CreditScoreVerificationEventsDialog;
